import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DocumentsGrid from './DocumentsGrid';
import DocumentsTable from './DocumentsTable';
import FilterBar from './FilterBar';
import { useDocumentSearch } from '@/hooks/useDocumentSearch';

interface DocumentsInterfaceProps {
  initialQuery?: string;
}

export default function DocumentsInterface({ initialQuery = '' }: DocumentsInterfaceProps) {
  const navigate = useNavigate();

  // View state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Search state
  const {
    documents,
    totalResults,
    searchTime,
    loading,
    error,
    searchQuery,
    selectedFilters,
    isSearchMode,
    setSearchQuery,
    executeSearch,
    addFilter,
    removeFilter,
    clearFilters,
    totalPages,
    currentPage,
    setPage
  } = useDocumentSearch({
    initialQuery,
    autoSearch: true,
    debounceMs: 500  // Increased debounce time to reduce rapid search triggers
  });

  // DocumentType options for filtering
  const documentTypeOptions = useMemo(() => [
    'contract', 'proposal', 'agreement', 'report', 'newsletter', 'memo', 'email', 'presentation', 'other'
  ], []);
  const [contentTypeFilter, setContentTypeFilter] = useState<string | null>(null);

  // Event handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  const handleDocumentClick = useCallback((id: string) => {
    navigate(`/documents/${id}`);
  }, [navigate]);

  const handleViewToggle = useCallback(() => {
    setViewMode(current => current === 'grid' ? 'list' : 'grid');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.length >= 2) {
      executeSearch();
      e.preventDefault();
    }
  }, [executeSearch, searchQuery]);

  // Handle content type filter change
  const handleContentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setContentTypeFilter(value !== '' ? value : null);
    if (value !== '') {
      addFilter({ field: 'metadata.classification.content.content_type', operator: 'eq', value });
    } else {
      // Remove all content_type filters
      selectedFilters
        .filter(f => f.field === 'metadata.classification.content.content_type')
        .forEach(f => removeFilter(f));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Search Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
          {/* Search Bar and View Toggle */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                placeholder="Search documents, organizations, and more..."
                className="pl-10"
              />
            </div>
            {/* Content Type Filter Dropdown */}
            <select
              value={contentTypeFilter || ''}
              onChange={handleContentTypeChange}
              className="border rounded px-3 py-2 text-sm text-gray-700 bg-white"
            >
              <option value="">All Types</option>
              {documentTypeOptions.map(type => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
            <Button
              variant="outline"
              size="icon"
              onClick={handleViewToggle}
              className="flex-shrink-0"
            >
              {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
            </Button>
          </div>

          {/* Active Filters */}
          {selectedFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <span className="text-sm text-gray-500">Filters:</span>
              {selectedFilters.map((filter, index) => (
                <Badge
                  key={`${filter.field}-${filter.value}-${index}`}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  <span>{filter.field}: {filter.value}</span>
                  <button
                    onClick={() => removeFilter(filter)}
                    className="ml-1 hover:text-red-500"
                  >
                    ×
                  </button>
                </Badge>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all
              </Button>
            </div>
          )}

          {/* Status Bar and Pagination Arrows */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div>
            {isSearchMode ? (
              searchQuery && (
                <span>
                  {loading
                    ? 'Searching...'
                    : `${totalResults} results found in ${searchTime.toFixed(2)}s`}
                </span>
              )
            ) : (
              <span>Recent Documents</span>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  className="p-2 rounded border bg-white text-gray-700 disabled:opacity-50"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 0}
                  aria-label="Previous Page"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  className="p-2 rounded border bg-white text-gray-700 disabled:opacity-50"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                  aria-label="Next Page"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Documents Display */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : viewMode === 'grid' ? (
          <DocumentsGrid
            documents={documents}
            loading={loading}
            onDocumentClick={handleDocumentClick}
          />
        ) : (
          <DocumentsTable
            documents={documents}
            mode={isSearchMode ? 'search' : 'recent'}
            loading={loading}
            onDocumentClick={handleDocumentClick}
          />
        )}
      </div>
    </div>
  );
}